import { Form, Row, Col, Button } from 'react-bootstrap';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { fetchEvents } from '../../../../../../../redux/slices/eventsSlice';
import { fetchSchedulingWindows } from '../../../../../../../redux/slices/scheduling_windowsSlice';
import { RootState } from '../../../../../../../redux/store';
import { LoadingSelect } from 'components/forms/LoadingFields';
import { toast } from 'react-toastify';

const Events = ({ state, setState, onChange, disabled = false }) => {
  const dispatch = useAppDispatch();
  const { wait_events = [], raise_events = [] } = state;

  const eventsState = useSelector((state: RootState) => state.events);
  const allEvents = eventsState.items || [];
  const eventsStatus = eventsState.status;
  const isEventsLoading = eventsStatus === 'loading' || eventsState.loading.fetchEvents;

  const processEvents = allEvents.filter((event) => event.type === 'processEvent');

  const schedulingWindowState = useSelector((state: RootState) => state.schedulingWindow);
  const schedulingWindows = schedulingWindowState.items || [];
  const isSchedulingWindowsLoading = schedulingWindowState.loading.fetchSchedulingWindows;

  const nextId = useRef(1);

  useEffect(() => {
    dispatch(fetchEvents({ limit: 100 }) as any);
    dispatch(fetchSchedulingWindows({}) as any);
  }, [dispatch]);

  // Add IDs to existing wait events if they don't have one
  useEffect(() => {
    if (wait_events.some((event) => !event.id)) {
      setState({
        ...state,
        wait_events: wait_events.map((event) => ({
          ...event,
          id: event.id || nextId.current++,
        })),
      });
    }
  }, [wait_events]); // keep deps as in your original code

  const addEmptyWaitEvent = () => {
    const hasDefaultQueue = Boolean(state.default_queue_path);
    setState({
      ...state,
      wait_events: [
        ...wait_events,
        {
          id: nextId.current++,
          path: null,
          auto_submit: hasDefaultQueue,
          auto_submit_mode: hasDefaultQueue ? 'if_none_pending' : null,
          auto_clear: true,
          scheduling_window: null,
          precondition: null,
        },
      ],
    });
  };

  const updateWaitEvent = (eventId, field, value) => {
    setState({
      ...state,
      wait_events: wait_events.map((event) => (event.id === eventId ? { ...event, [field]: value } : event)),
    });
  };

  // The dropdown collapses the two wire fields (auto_submit + auto_submit_mode)
  // into one of three states; 'off' clears the mode (the backend rejects a mode
  // without auto_submit). Any submitting state needs a default queue.
  const setAutoSubmitSelection = (eventId, selection) => {
    if (selection !== 'off' && !state.default_queue_path) {
      toast.warning('Auto Submit requires a default queue. Please assign a default queue in the Properties tab first.', {
        toastId: 'auto-submit-requires-default-queue',
      });
      return;
    }
    const patch = selection === 'off' ? { auto_submit: false, auto_submit_mode: null } : { auto_submit: true, auto_submit_mode: selection };
    setState({
      ...state,
      wait_events: wait_events.map((event) => (event.id === eventId ? { ...event, ...patch } : event)),
    });
  };

  const removeWaitEvent = (eventId) => {
    setState({
      ...state,
      wait_events: wait_events.filter((event) => event.id !== eventId),
    });
  };

  const updateRaiseEvent = (eventIndex, field, value) => {
    setState({
      ...state,
      raise_events: raise_events.map((event, index) => (index === eventIndex ? { ...event, [field]: value } : event)),
    });
  };

  const removeRaiseEvent = (eventIndex) => {
    setState({
      ...state,
      raise_events: raise_events.filter((_, index) => index !== eventIndex),
    });
  };

  return (
    <>
      <Row className="mb-4">
        <Col>
          <h4>Wait Events</h4>
          <div className="mb-3">
            {wait_events.map((event, index) => (
              <div key={event.id ?? `wait-event-${index}`} className="d-flex align-items-center mb-2">
                <LoadingSelect
                  name={`wait_event_path_${event.id}`}
                  value={event.path || ''}
                  onChange={(e) => updateWaitEvent(event.id, 'path', e.target.value || null)}
                  disabled={disabled}
                  className="me-2"
                  style={{ width: '200px' }}
                  isLoading={isEventsLoading}
                  options={(processEvents || []).map((ev: any) => ({
                    value: `${ev.zone_name}|${ev.name}`,
                    label: `${ev.name} (${ev.zone_name})`,
                  }))}
                  placeholder="Select event..."
                  loadingLabel="Loading events..."
                />

                <Form.Select
                  aria-label={`wait_event_auto_submit_${event.id}`}
                  value={event.auto_submit ? (event.auto_submit_mode ?? 'if_none_pending') : 'off'}
                  onChange={(e) => setAutoSubmitSelection(event.id, e.target.value)}
                  disabled={disabled}
                  className="me-2"
                  style={{ width: '200px' }}
                >
                  <option value="off">No auto-submit</option>
                  <option value="if_none_pending">Submit if none pending</option>
                  <option value="always">Always submit</option>
                </Form.Select>

                <Form.Check
                  type="checkbox"
                  label="Auto Clear"
                  checked={event.auto_clear ?? true}
                  onChange={(e) => updateWaitEvent(event.id, 'auto_clear', e.target.checked)}
                  disabled={disabled}
                  className="me-2"
                />

                <LoadingSelect
                  name={`wait_event_scheduling_window_${event.id}`}
                  value={event.scheduling_window || ''}
                  onChange={(e) => updateWaitEvent(event.id, 'scheduling_window', e.target.value || null)}
                  disabled={disabled}
                  className="me-2"
                  style={{ width: '200px' }}
                  isLoading={isSchedulingWindowsLoading}
                  options={(schedulingWindows || []).map((window: any) => ({
                    value: `${window.zone_name}|${window.name}`,
                    label: `${window.name} (${window.zone_name})`,
                  }))}
                  placeholder="No scheduling window"
                  loadingLabel="Loading scheduling windows..."
                />

                <Form.Control
                  type="text"
                  value={event.precondition ?? ''}
                  onChange={(e) => updateWaitEvent(event.id, 'precondition', e.target.value || null)}
                  disabled={disabled}
                  className="me-2"
                  style={{ width: '260px' }}
                  placeholder="Precondition: true or =expression"
                  aria-label={`wait_event_precondition_${event.id}`}
                />

                <Button variant="outline-secondary" size="sm" onClick={() => removeWaitEvent(event.id)} disabled={disabled}>
                  X
                </Button>
              </div>
            ))}
          </div>
          <Button onClick={addEmptyWaitEvent} disabled={disabled}>
            Add wait event
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <h4>Raise Events</h4>
          <div className="mb-3">
            {raise_events.map((event, index) => (
              <div key={event.id ?? event.path ?? `raise-event-${index}`} className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <LoadingSelect
                  name={`raise_event_path_${index}`}
                  value={event.path || ''}
                  onChange={(e) => {
                    const newPath = e.target.value || null;
                    updateRaiseEvent(index, 'path', newPath);
                  }}
                  disabled={disabled}
                  className="m-0"
                  style={{ width: '200px' }}
                  isLoading={isEventsLoading}
                  options={(processEvents || []).map((ev: any) => ({
                    value: `${ev.zone_name}|${ev.name}`,
                    label: `${ev.name} (${ev.zone_name})`,
                  }))}
                  placeholder="Select event..."
                  loadingLabel="Loading events..."
                />

                <Form.Control
                  type="text"
                  value={event.raiser_comment ?? ''}
                  onChange={(e) => updateRaiseEvent(index, 'raiser_comment', e.target.value)}
                  disabled={disabled}
                  className="m-0"
                  style={{ width: '260px' }}
                  placeholder="Comment or =expression"
                  aria-label={`raise_event_comment_${index}`}
                />

                <Form.Control
                  type="text"
                  value={event.precondition ?? ''}
                  onChange={(e) => updateRaiseEvent(index, 'precondition', e.target.value)}
                  disabled={disabled}
                  className="m-0"
                  style={{ width: '260px' }}
                  placeholder="Precondition: true or =expression"
                  aria-label={`raise_event_precondition_${index}`}
                />

                <Button variant="outline-secondary" size="sm" onClick={() => removeRaiseEvent(index)} disabled={disabled}>
                  X
                </Button>
              </div>
            ))}
          </div>
          <Button
            onClick={() => {
              setState({
                ...state,
                raise_events: [...raise_events, { path: null, raiser_comment: '', precondition: '' }],
              });
            }}
            disabled={disabled}
          >
            Add raise event
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default Events;
